import type {
  ComparisonData,
  ComparisonGroup,
  ComparisonGroupId,
  ComparisonRow,
  Competitor,
  CompetitorId,
  SupportLevel,
} from './types';
import rawData from '../../specs/homepage/inputs/smith-comparison-data.json';

const ALLOWED_GROUP_IDS = new Set<ComparisonGroupId>(['core', 'development', 'agency']);

type RawSupport = Record<string, string>;

type RawFeature = {
  name: string;
  support: RawSupport;
};

type RawGroup = {
  id: string;
  name: string;
  description: string;
  note?: string;
  features: RawFeature[];
};

function isAllowedGroup(id: string): id is ComparisonGroupId {
  return ALLOWED_GROUP_IDS.has(id as ComparisonGroupId);
}

function normalizeSupport(raw: RawSupport): Record<CompetitorId, SupportLevel> {
  const result: Record<CompetitorId, SupportLevel> = {
    smith: 'no',
    'managed-agents': 'no',
    gsd: 'no',
    superpowers: 'no',
    'ralph-loop': 'no',
    'claude-mem': 'no',
    'claude-skills': 'no',
  };
  for (const id of Object.keys(result) as CompetitorId[]) {
    const value = raw[id];
    if (value === 'yes' || value === 'partial' || value === 'no') {
      result[id] = value;
    }
  }
  return result;
}

function transformGroup(group: RawGroup): ComparisonGroup | null {
  if (!isAllowedGroup(group.id)) return null;
  const rows: ComparisonRow[] = group.features.map((feature) => ({
    featureName: feature.name,
    support: normalizeSupport(feature.support),
  }));
  return {
    id: group.id,
    name: group.name,
    description: group.description,
    note: group.note,
    rows,
  };
}

const competitors: Competitor[] = rawData.competitors.map((c) => ({
  id: c.id as CompetitorId,
  name: c.name,
  url: c.url ?? null,
  emphasized: c.emphasized,
}));

const groups: ComparisonGroup[] = (rawData.groups as RawGroup[])
  .map(transformGroup)
  .filter((g): g is ComparisonGroup => g !== null);

export const comparisonData: ComparisonData = {
  lastVerified: rawData.meta.lastVerified,
  competitors,
  groups,
};

export default comparisonData;
